import React, { createRef, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import throttle from 'lodash.throttle';
import { IconButton } from '../index';
import styles from './calendar-slider.module.scss';
import { useIsMedium, useIsScrollable } from '../../hooks';

// Optimizations TBD:
// 1. The Animation could be fine tuned by not only using one width value for all month elements,
// but we could use the width of the months previous element when scrolling left and the width of
// the months next element when scrolling right.

export type CalendarSliderItemType = {
  month: string;
  dayNumerical: number;
  dayAlphabetical: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  selected: boolean;
  disabled?: boolean;
};

export type CalendarSliderProps = {
  items: CalendarSliderItemType[];
  ariaLabels: { iconButtonLeft: string; iconButtonRight: string };
  className?: string;
};

const handleClickRight = (ref: React.MutableRefObject<HTMLUListElement | null>) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (ref.current) {
    // 144 is 3 times the width of a date item
    ref.current.scrollLeft += 144;
  }
};

const handleClickLeft = (ref: React.MutableRefObject<HTMLUListElement | null>) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (ref.current) {
    // 144 is 3 times the width of a date item
    ref.current.scrollLeft -= 144;
  }
};

const gradientVisibility = (indicators: { left: boolean; right: boolean }) => {
  const { left, right } = indicators;

  if (left && !right) {
    return 'left';
  }

  if (right && !left) {
    return 'right';
  }

  if (right && left) {
    return 'both';
  }
};

export const CalendarSlider: React.FC<CalendarSliderProps> = ({ className, items, ariaLabels }) => {
  const ref = useRef<HTMLUListElement>(null);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [maxMonthWidth, setMaxMonthWidth] = useState(0);
  const [indicator, setIndicator] = useState({ left: false, right: true });
  const [initialMonthXPosition, setInitialMonthXPosition] = useState<number[]>([]);
  const monthRefs = React.useRef<React.MutableRefObject<HTMLDivElement>[]>([]);

  monthRefs.current = items.map((_, i) => monthRefs.current[i] ?? createRef());

  const isScrollable = useIsScrollable(ref);
  const isMedium = useIsMedium();

  useEffect(() => {
    // get the initial x positions of the months.
    if (!ref.current) {
      return;
    }

    const listXPosition = ref.current.getBoundingClientRect().x;
    const relativeMonthXPosition = monthRefs.current.map((monthRef) =>
      Math.round(monthRef.current?.getBoundingClientRect().x - listXPosition)
    );

    setInitialMonthXPosition(relativeMonthXPosition);
  }, []);

  useEffect(() => {
    const widths = monthRefs.current.filter((el) => el.current?.clientWidth);
    const maxWidth = Math.max(...widths.map((el) => el.current.clientWidth));

    setMaxMonthWidth(maxWidth);
  }, [isMedium]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const el = ref.current;

    const handleScroll = throttle(() => {
      const hasLeftIndicator = el.scrollLeft > 0;
      const hasRightIndicator = el.scrollWidth - el.clientWidth > el.scrollLeft;

      setIndicator({ left: hasLeftIndicator, right: hasRightIndicator });

      const arrowWidth = 40;
      const threshold = isMedium ? maxMonthWidth - arrowWidth : maxMonthWidth;

      items.forEach((month, i) => {
        if (month.dayNumerical !== 1 && i !== 0) {
          return;
        }

        if (el.scrollLeft >= initialMonthXPosition[i] - threshold) {
          return setCurrentMonth(i);
        }
      });
    }, 100);

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  });

  return (
    <div className={classnames(styles.calendarSlider, className)} data-testid="calendarslider-component">
      <div className={styles.sliderContainer}>
        {isScrollable && (
          <IconButton
            className={classnames(styles.arrow, styles.left)}
            ariaLabel={ariaLabels.iconButtonLeft}
            iconName="chevron-left--small"
            variant="ghost"
            size="200"
            onClick={() => handleClickLeft(ref)}
            disabled={!indicator.left}
          />
        )}
        <ul className={classnames(styles.list, styles[`gradient--${gradientVisibility(indicator)}`])} ref={ref}>
          {items.map((item, i) => {
            // get the month of the first item for a start a OR if it is the 1st day of a (new) month
            const itemHasMonth = i === 0 || item.dayNumerical === 1;
            return (
              <li
                key={item.month + i + item.dayNumerical}
                className={classnames(styles.item, {
                  [styles.selected]: item.selected,
                  [styles.disabled]: item.disabled
                })}
              >
                <button
                  className={classnames(styles.button, {
                    [styles.current]: currentMonth === i,
                    [styles.disabled]: item.disabled
                  })}
                  onClick={item.onClick}
                  aria-pressed={item.selected}
                >
                  {itemHasMonth && (
                    <div className={styles.month} ref={monthRefs.current[i]}>
                      {item.month}
                    </div>
                  )}
                  <div className={styles.day}>{item.dayAlphabetical.toUpperCase()}</div>
                  <div className={styles.circle}>
                    <span className={styles.dayNumber}>{item.dayNumerical}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {isScrollable && (
          <IconButton
            className={classnames(styles.arrow, styles.right)}
            ariaLabel={ariaLabels.iconButtonRight}
            iconName="chevron-right--small"
            variant="ghost"
            size="200"
            onClick={() => handleClickRight(ref)}
            disabled={!indicator.right}
          />
        )}
      </div>
    </div>
  );
};
